import { toast } from "react-toastify";
import {
  EntityType,
  FileType,
  MetadataSource,
  MetadataType,
  ModelTypes,
  NaturalDimensions,
  RawFeatureConfig,
  TransformedFeatureConfig,
  UIContext,
} from "types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { SHOW_DS_VERSION } from "helpers/constants/filters";
import VLLogo from "assets/img/newVLicon.svg";
import {
  DATA_PAGE_PARAMETER,
  ENTITY_TYPE_PARAMETER,
  FLAT_RESPONSE_PARAMETER,
  FLOW_PARAMETER,
  GRANULARITY_PARAMETER,
  METADATA_SOURCES_PARAMETER,
  PAGE_PARAMETER,
  SEED_PARAMETER,
} from "helpers/constants/miscellaneous";

export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }

  return new Promise((resolve, reject) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      const successful = document.execCommand("copy"); // deprecated but fallback
      document.body.removeChild(textarea);
      successful ? resolve() : reject(new Error("Copy failed"));
    } catch (err) {
      document.body.removeChild(textarea);
      reject(err);
    }
  });
}

export function addQueryParameter(
  url: string,
  paramName: string,
  paramValue: any,
) {
  const separator = url.includes("?") ? "&" : "?";
  return (
    url +
    separator +
    encodeURIComponent(paramName) +
    "=" +
    encodeURIComponent(paramValue)
  );
}

export function convertURItoArray(str: string) {
  const result = JSON.parse(str);

  if (Array.isArray(result)) {
    return result;
  } else {
    return false;
  }
}

export function convertURItoNumber(str: string) {
  const result = decodeURIComponent(JSON.parse(str));
  if (Number(result) || Number(result) === 0) {
    return Number(result);
  } else {
    return false;
  }
}

export function calculatePlaceHolderCount(
  cardContainer: any,
  cardElement: any,
  length: number,
  gap: number,
) {
  let wrapperWidth = 0;
  let elementWidth = 0;

  if (cardContainer) {
    wrapperWidth = cardContainer.offsetWidth;
  }

  if (cardElement) {
    elementWidth = cardElement.offsetWidth;
  }

  const cols = Math.floor((wrapperWidth - gap) / elementWidth);
  const rows = Math.ceil(length / cols);

  const placeholdersCount: any = cols * rows - length;

  return isNaN(parseInt(placeholdersCount)) ? 32 : placeholdersCount;
}

export function calculateAspectRatio(img: any) {
  let ratio = img.naturalWidth / img.naturalHeight;
  let width = img.height * ratio;
  let height = img.height;
  if (width > img.width) {
    width = img.width;
    height = img.width / ratio;
  }
  return [height, width];
}

export function debounceCreator(func: any, delay: number) {
  let timeoutID: ReturnType<typeof setTimeout>;
  return (...args: any) => {
    clearTimeout(timeoutID);
    timeoutID = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

export function isMobileDevice() {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a,
      ) ||
      // eslint-disable-next-line no-useless-escape
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
  })(navigator.userAgent);
  return check;
}

export function showNotification(type: String, value: String) {
  switch (type) {
    case "error":
      toast.error(value);
      break;
    case "info":
      toast.info(value);
      break;
    case "success":
      toast.success(value);
      break;
    case "warning":
      toast.warning(value);
      break;
  }
}

export function useWindowResize() {
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);

  //delaying resize recalls  by 0.7 seconds
  const debouncedListener = debounceCreator(() => {
    setWidth(window.innerWidth);
    setHeight(window.innerHeight);
  }, 700);

  useEffect(() => {
    window.addEventListener("resize", debouncedListener);
    return () => {
      window.removeEventListener("resize", debouncedListener);
    };
    //eslint-disable-next-line
  }, []);

  return {
    width,
    height,
  };
}

export function addOpacity(randomBrightColor: string, alpha: number) {
  const rgbArray = randomBrightColor.match(/\d+/g);
  if (rgbArray && rgbArray.length === 3) {
    const r = parseInt(rgbArray[0]);
    const g = parseInt(rgbArray[1]);
    const b = parseInt(rgbArray[2]);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(0, 0, 0, ${alpha})`;
}

export function generateRandomColor() {
  // adjust value for brightness
  const minComponentValue = 115;
  const red =
    minComponentValue +
    Math.floor(Math.random() * (255 - minComponentValue + 1));
  const green =
    minComponentValue +
    Math.floor(Math.random() * (255 - minComponentValue + 1));
  const blue =
    minComponentValue +
    Math.floor(Math.random() * (255 - minComponentValue + 1));
  const randomBrightColor = `rgb(${red}, ${green}, ${blue})`;
  return randomBrightColor;
}
export function safeJsonParse(jsonString: string) {
  try {
    const parsedData = JSON.parse(jsonString);

    if (Array.isArray(parsedData)) {
      return parsedData;
    } else {
      throw new Error();
    }
  } catch (error) {
    return false;
  }
}

export function useInterval(callback: any, delay: number) {
  const savedCallback = useRef<any>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useDatasetVersion(dsVersion: string | null) {
  const showDsVersion = useFeatureFlagService(SHOW_DS_VERSION).enabled;
  let datasetPipelineVersion = null;

  // check if dsVersion is semver
  if (
    showDsVersion &&
    dsVersion &&
    /^\d+\.\d+([a-zA-Z]*)?(\.\d+(-[a-zA-Z]+\d*)?)?$/.test(dsVersion)
  ) {
    datasetPipelineVersion = dsVersion;
  }
  return datasetPipelineVersion;
}

export function truncateFilename(filename: string, maxLength: number) {
  if (filename.length <= maxLength) return filename;

  const extensionIndex = filename.lastIndexOf(".");
  const nameWithoutExtension = filename.substring(0, extensionIndex);
  const extension = filename.substring(extensionIndex);

  const remainingLength = maxLength - 3;

  const halfLength = Math.floor(remainingLength / 2);
  const beginning = nameWithoutExtension.substring(0, halfLength);
  const end = nameWithoutExtension.substring(
    nameWithoutExtension.length - halfLength,
  );

  return `${beginning}...${end}${extension}`;
}

export const checkTallOrWideImage = async (
  url: string,
): Promise<"tall" | "wide"> => {
  const img = new Image();

  const imageLoaded = new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = (error) => {
      console.error(`Error loading image for tall/wide check: ${url}`, error);
      reject(error);
    };
  });

  img.src = url;

  await imageLoaded;

  const width = img.width;
  const height = img.height;

  const isTall = height > width * 1.25;

  return isTall ? "tall" : "wide";
};

export const getMediaDimensions = async (
  url: string,
): Promise<NaturalDimensions> => {
  return new Promise<NaturalDimensions>((resolve, reject) => {
    const media: HTMLImageElement = new Image();

    media.onload = () => {
      resolve({
        naturalWidth: media.naturalWidth,
        naturalHeight: media.naturalHeight,
      });
    };

    media.onerror = (error) => {
      console.error(
        `Error loading image for dimension calculation: ${url}`,
        error,
      );
      reject(error);
    };

    media.src = url;
  });
};

export const useHoverDelay = (action: () => void, delay: number) => {
  // This ref will store the timeout ID.
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      action();
    }, delay);
  }, [action, delay, clearTimer]);

  const handleMouseLeave = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  return { handleMouseEnter, handleMouseLeave };
};

export default useHoverDelay;

export const isFastdupUser = () => process.env.REACT_APP_MODE === "FD2";
export const isOnPremUser = () => {
  const env = process.env.REACT_APP_ENVIRONMENT;
  const trimmedEnv = env?.trim().replace(/"/g, "");
  return trimmedEnv === "ON_PREM";
};

export const transformFilterData = (
  features: RawFeatureConfig[] | undefined,
): TransformedFeatureConfig[] => {
  if (!features) {
    return [];
  }
  const transformedFeatures: TransformedFeatureConfig[] = [];
  features.forEach((feature) => {
    const existingFeature = transformedFeatures.find(
      (transformedFeature) =>
        transformedFeature.feature_key === feature.feature_key,
    );
    const context = feature.contexts[0] as UIContext;

    if (existingFeature) {
      existingFeature.feature_behavior[context] = feature.feature_behavior;
    } else {
      transformedFeatures.push({
        feature_key: feature.feature_key,
        feature_behavior: { [context]: feature.feature_behavior },
        featureOptions: feature.feature_options,
        reason: feature.reason,
      });
    }
  });

  return transformedFeatures;
};

export function determineFileType(filename: string | undefined): FileType {
  if (!filename) {
    return FileType.UNKNOWN;
  }

  const extension = filename.split(".").pop()?.toLowerCase();
  const videoExtensions = ["mp4", "avi", "mkv", "mov", "wmv"];
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp"];

  if (extension && videoExtensions.includes(extension)) {
    return FileType.VIDEO;
  } else if (extension && imageExtensions.includes(extension)) {
    return FileType.IMAGE;
  } else {
    return FileType.UNKNOWN;
  }
}

export const formatSize = (bytes: number): string => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.ceil(bytes / Math.pow(1024, i)) + " " + sizes[i];
};

export const removeS3Prefix = (s3link: string): string => {
  // Extract the substring starting from "s3://"
  if (s3link.includes("s3://")) {
    const s3Index = s3link.indexOf("s3://");
    return s3link.substring(s3Index + 5);
  }
  return s3link;
};

export const isValidEmail = (email: string): boolean => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
};

export const getModelTypeColor = (
  modelType: ModelTypes,
): { color: string; backgroundColor: string } => {
  switch (modelType) {
    case ModelTypes.IMAGE_TAGGING:
      return { color: "#85cb98", backgroundColor: "#85cb9833" };
    case ModelTypes.CAPTION_IMAGES:
      return { color: "#6dc2e7", backgroundColor: "#6dc2e733" };
    case ModelTypes.CAPTION_OBJECTS:
      return { color: "#e46a9e", backgroundColor: "#e46a9e33" };
    case ModelTypes.OBJECT_DETECTION:
      return { color: "#ff9a06", backgroundColor: "#ff9a0633" };
    case ModelTypes.OBJECT_TAGGING:
      return { color: "#85cb98", backgroundColor: "#85cb9833" };
    case ModelTypes.MULTIMODEL_IMAGE_ENCODING:
    case ModelTypes.MULTIMODEL_OBJECT_ENCODING:
      return { color: "#e099ff", backgroundColor: "#e099ff33" };
    case ModelTypes.INSTANCE_RETRIEVAL:
      return { color: "#dede3e", backgroundColor: "#dede3e33" };
    default:
      return { color: "#000000", backgroundColor: "#00000033" };
  }
};

export const getVendorLogo = (vendorLogoUri: string) => {
  if (!vendorLogoUri) return VLLogo; // Default logo

  try {
    return require(`assets/img/${vendorLogoUri}`);
  } catch (error) {
    console.error("Error loading image:", error);
    return VLLogo; // Fallback in case of error
  }
};

export const getInitialMDType = (
  globalMDType: MetadataType | null,
  tags: string[],
  labels: string[],
  captions: string[],
  entityParams: EntityType | null,
  hideMetadataOptions: boolean,
) => {
  if (hideMetadataOptions && globalMDType) {
    return globalMDType;
  }
  const isObjectEntitySelected = entityParams === "OBJECTS";

  if (globalMDType) {
    if (isObjectEntitySelected && globalMDType === MetadataType.IMAGE_LABELS) {
      return MetadataType.OBJECT_LABELS;
    } else if (
      !isObjectEntitySelected &&
      globalMDType === MetadataType.OBJECT_LABELS
    ) {
      return MetadataType.IMAGE_LABELS;
    } else if (
      isObjectEntitySelected &&
      globalMDType === MetadataType.IMAGE_CAPTIONS
    ) {
      return MetadataType.OBJECT_CAPTIONS;
    } else if (
      !isObjectEntitySelected &&
      globalMDType === MetadataType.OBJECT_CAPTIONS
    ) {
      return MetadataType.IMAGE_CAPTIONS;
    } else {
      return globalMDType;
    }
  }

  if (labels.length > 0) {
    return isObjectEntitySelected
      ? MetadataType.OBJECT_LABELS
      : MetadataType.IMAGE_LABELS;
  } else if (captions.length > 0) {
    return isObjectEntitySelected
      ? MetadataType.OBJECT_CAPTIONS
      : MetadataType.IMAGE_CAPTIONS;
  } else if (tags.length > 0) {
    return MetadataType.USER_TAGS;
  } else {
    return isObjectEntitySelected
      ? MetadataType.OBJECT_LABELS
      : MetadataType.IMAGE_LABELS;
  }
};

export function toQueryString(obj: Object): string {
  const queryParams = Object.entries(obj)
    .filter(([_key, value]) => value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`,
    );

  return queryParams.join("&");
}

export function formatCapsString(input: string): string {
  if (!input) return "";
  const lowerCased = input.toLowerCase();
  return lowerCased.charAt(0).toUpperCase() + lowerCased.slice(1);
}

export const checkIsUnselectable = (
  selectedValue: MetadataSource,
  models: MetadataSource[],
  sources: MetadataSource[],
) => {
  const annotationSource = sources[0] || null;
  const isAnnotationSource = annotationSource?.id === selectedValue.id;

  // If only one model or source is available, make it unselectable
  const isOnlyOneAvailable =
    (models.length === 0 && sources.length <= 1) ||
    (sources.length === 0 && models.length <= 1);

  if (isOnlyOneAvailable) return true;

  // If it's not the annotation source and has no preview, it's unselectable
  if (!isAnnotationSource && !selectedValue.isSelectable) return true;

  const searchParams = new URLSearchParams(window.location.search);
  const isObjectEntity = searchParams.get(ENTITY_TYPE_PARAMETER) === "OBJECTS";

  // Outside object entity view, all are selectable
  if (!isObjectEntity) return false;

  const objectDetectorModel = models.find(
    (model) => model.type === ModelTypes.OBJECT_DETECTION,
  );
  const objectCaptionerModel = models.find(
    (model) => model.type === ModelTypes.CAPTION_OBJECTS,
  );

  const isAnnotationSelected = !!annotationSource?.selected;
  const isCaptionerSelected = !!objectCaptionerModel?.selected;
  const isObjectDetectorSelected = !!objectDetectorModel?.selected;

  // If trying to select object detector
  if (selectedValue.type === ModelTypes.OBJECT_DETECTION) {
    if (!annotationSource) return true; // needs annotation source
    if (isCaptionerSelected) return true; // cannot be unselected without captioner

    return !isAnnotationSelected; // only selectable if annotation is selected
  }

  // If trying to select object captioner
  if (selectedValue.type === ModelTypes.CAPTION_OBJECTS) {
    return !isObjectDetectorSelected; // needs object detector selected
  }

  // If trying to select annotation source
  if (isAnnotationSource) {
    const noModels = models.length === 0;
    const detectorNotSelected =
      !!objectDetectorModel && !isObjectDetectorSelected;

    // Requires at least one model and object detector selected
    return noModels || detectorNotSelected;
  }

  // Default: selectable
  return false;
};

export const checkAnyFilterApplied = (
  searchParams: URLSearchParams,
): boolean => {
  const otherParams = Array.from(searchParams.keys()).filter(
    (key) =>
      ![
        DATA_PAGE_PARAMETER,
        PAGE_PARAMETER,
        GRANULARITY_PARAMETER,
        ENTITY_TYPE_PARAMETER,
        FLAT_RESPONSE_PARAMETER,
        FLOW_PARAMETER,
        SEED_PARAMETER,
        METADATA_SOURCES_PARAMETER,
      ].includes(key),
  );

  return otherParams.length > 0;
};

export function getTextWidth(text: string, fontSize?: string): number {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Unable to get canvas context");
  }

  context.font = `${fontSize || "12px"} Inter`;
  const metrics = context.measureText(text);
  return metrics.width;
}

export function getFalseRangeDisplayValue(
  value: number,
  minValue: number,
): number {
  const normalized = (value - minValue) / (1 - minValue);
  return Math.round(normalized * 100) / 100; //to 2 decimal places
}
